import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Star } from "lucide-react";
import { REVIEWS } from "@/content";
import { Reveal } from "@/components/landing/Reveal";

const SalesCounter = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    let timeout;
    const run = () => {
      let start = null;
      const step = (t) => {
        if (start === null) start = t;
        const p = Math.min((t - start) / 2400, 1);
        setValue(Math.round(p * 100));
        if (p < 1) raf = requestAnimationFrame(step);
        else timeout = setTimeout(run, 3000);
      };
      raf = requestAnimationFrame(step);
    };
    run();
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [inView]);

  return (
    <div ref={ref} data-testid="sales-counter" className="mt-16">
      <p className="font-mono text-xs uppercase tracking-[0.35em] text-zinc-400">Over</p>
      <p className="mt-2 font-display text-6xl font-extrabold tracking-tight sm:text-8xl">
        ${value}K+
      </p>
      <p className="mt-2 text-xs uppercase tracking-[0.3em] text-zinc-500">In sales and counting</p>
    </div>
  );
};

export const Reviews = () => (
  <section id="reviews" data-testid="reviews-section" className="relative scroll-mt-28 border-t border-white/10 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-zinc-400">Client reviews</p>
        <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl">
          Five stars. <span className="text-zinc-500">Every time.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <SalesCounter />
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {REVIEWS.map((review, i) => (
          <Reveal key={review.name} delay={(i % 3) * 0.08}>
            <figure
              data-testid={`review-card-${review.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-8 transition-[border-color,background-color] duration-500 hover:border-white/25"
            >
              <div className="flex gap-1" aria-label="5 out of 5 stars">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-white text-white" />
                ))}
              </div>
              <blockquote className="mt-6 flex-1 text-sm leading-relaxed text-zinc-300">
                "{review.quote}"
              </blockquote>
              <figcaption className="mt-6 border-t border-white/5 pt-4">
                <p className="text-sm font-semibold">{review.name}</p>
                <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                  {review.service}
                </p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
